import React, { useEffect, useReducer, useState } from 'react';

import {
  InsightV2ApiInsightV2Request,
  NativeResources,
} from '@ariksa/inventory-core/api';
import { Box, Stack, HStack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import {
  each,
  filter,
  forEach,
  isEmpty,
  isEqual,
  sortBy,
  toArray,
} from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { QueryStatus } from 'services/types';

import { InventoryService } from 'api/services';
import { Card, getIcon, PageHeaderWithIcon } from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { AddButton, ButtonGroup, Select } from 'components/DataEntry';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { DataScanIcon, OutpostForVulnerabilityIcon } from 'components/Icons';
import { Menu } from 'components/Overlay';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { DetachAccountModal } from 'containers/Setup/Outpost/Components/DetachAccountModal';
import { useOutpostTableColumns } from 'containers/Setup/Outpost/Components/hooks/useOutpostTableColumns';
import { OutpostSubComponent } from 'containers/Setup/Outpost/Components/OutpostSubComponent';
import { UpdateRegionsModal } from 'containers/Setup/Outpost/Components/UpdateRegionsModal';
import {
  outpostStatusOptions,
  outpostTypeOptions,
} from 'containers/Setup/Outpost/Components/utils';
import { selectOutpost } from 'containers/Setup/Outpost/selectors';

import { actions } from '../slice';

export const OutpostTable: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { vulnerabilityOutposts, dataOutposts, ariksaOutposts } = useSelector(
    selectOutpost,
  );

  const { accountMapping } = useCloudAccountId();
  const { accountEnvironmentMapping } = useEnvironmentOptions();
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [filteredData, setFilteredData] = useState<Record<string, any>[]>([]);
  const {
    columns,
    accountsColumns,
    viewBy,
    setViewBy,
    outpostViewByOptions,
    updateRegionsDisclosure,
    detachAccountDisclosure,
    currentRow,
  } = useOutpostTableColumns();
  const { setLearnMore } = useKnowledgeBase();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      status: outpostStatusOptions[0],
      region: { label: 'Region', value: undefined },
      regionOptions: [],
      outpostType: outpostTypeOptions[0],
      account: { label: 'Account', value: undefined },
      accountOptions: [],
      sortByField: {
        sortField: 'last_seen',
        sortOrder: 'desc',
      },
      sortedData: [],
      row: {},
      setGlobalRegions: false,
    },
  );

  useEffect(() => {
    if (viewBy.value !== 'Ariksa') {
      dispatch(actions.getVulnerabilityScanners({ q: {} }));
      dispatch(actions.getDataScanners({ q: {} }));
    } else {
      dispatch(actions.getAriksaOutposts({}));
      dispatch(
        actions.getEnabledRegionsForScanning({ q: { provider: 'aws' } }),
      );
    }
  }, [dispatch, viewBy]);

  useEffect(() => {
    let items: Record<string, any>[] = [];
    forEach(dataOutposts.data, (o, id) => {
      const account = accountMapping?.[id];
      const environment = accountEnvironmentMapping?.[id];

      let outposts: Record<string, any>[] = [];
      each(o, outpost => {
        if (!!outpost?.instance_id)
          outposts.push({
            ...outpost,
            type: 'Data',
            cloud: account?.cloud_type,
            account_name: account?.name,
            cloud_account_id: account?.cloud_account_id,
            account_id: id,
            environment_name: environment?.name,
            native_name: o?.native_type,
          });
      });

      items = [...items, ...outposts];
    });
    forEach(vulnerabilityOutposts.data, (o, id) => {
      const account = accountMapping?.[id];
      const environment = accountEnvironmentMapping?.[id];

      let outposts: Record<string, any>[] = [];
      each(o, outpost => {
        if (!!outpost?.scanner_id)
          outposts.push({
            ...outpost,
            type: 'Vulnerability',
            cloud: account?.cloud_type,
            account_name: account?.name,
            cloud_account_id: account?.cloud_account_id,
            account_id: id,
            environment_name: environment?.name,
            instance_id: outpost?.scanner_id,
          });
      });

      items = [...items, ...outposts];
    });
    setData(items);
  }, [
    dataOutposts.data,
    accountMapping,
    accountEnvironmentMapping,
    vulnerabilityOutposts.data,
  ]);

  useEffect(() => {
    if (isEmpty(data)) return;
    let resources: Record<string, any> = {};
    each(data, o => {
      if (!o?.native_name || !o?.instance_id) return;
      if (!!resources?.[o?.native_name]) {
        resources[o?.native_name] = {
          ids: [...resources?.[o?.native_name]?.ids, o?.instance_id],
          account_ids: [
            ...resources?.[o?.native_name]?.account_ids,
            o.account_id,
          ],
        };
      } else {
        resources[o?.native_name] = {
          ids: [o?.instance_id],
          account_ids: [o.account_id],
        };
      }
    });

    //get resource details
    forEach(resources, (items, key) => {
      const insightPayload: InsightV2ApiInsightV2Request = {
        insightV2Request: {
          resource_type: key as NativeResources,
          resource_ids: items?.ids,
          account_id: items?.account_ids,
        },
      };
      dispatch(
        actions.getResourceDetails({
          q: insightPayload,
          status: QueryStatus.pending,
        }),
      );
      InventoryService.InsightV2.insightV2(insightPayload).then(res => {
        dispatch(
          actions.getResourceDetails({
            q: insightPayload,
            status: QueryStatus.fulfilled,
            data: res.data,
          }),
        );
      });
    });
  }, [data, dispatch]);

  //set region options
  useEffect(() => {
    let regions = new Set();
    each(data, o => regions.add(o.region));
    updateState({
      regionOptions: [
        { label: 'Region', value: undefined },
        ...map(toArray(regions), o => ({ label: o, value: o })),
      ],
    });
  }, [data]);

  //set account options
  useEffect(() => {
    let accounts = new Set();
    each(data, o => accounts.add(o.account_id));
    updateState({
      accountOptions: [
        { label: 'Account', value: undefined },
        ...map(toArray(accounts) as string[], o => ({
          label:
            accountMapping[o]?.name +
            ' (Account ID: ' +
            accountMapping[o]?.cloud_account_id +
            ')',
          value: o,
          icon: getIcon(accountMapping[o]?.cloud_type!),
        })),
      ],
    });
  }, [data, accountMapping]);

  //filter data
  useEffect(() => {
    const filtered_data = filter(
      data,
      o =>
        o.status === state.status?.value &&
        (!!state.region?.value ? o.region === state.region?.value : true) &&
        (!!state.account?.value
          ? o.account_id === state.account?.value
          : true) &&
        (!!state.outpostType?.value
          ? o.type === state.outpostType?.value
          : true),
    );
    setFilteredData(filtered_data);
  }, [data, state.status, state.region, state.outpostType, state.account]);

  //sort filtered data
  useEffect(() => {
    updateState({
      sortedData: sortBy(filteredData, d => -dayjs(d.last_seen)),
    });
  }, [filteredData]);

  const menuItems = [
    {
      label: 'CHOOSE OUTPOST TYPE',
      group: {
        title: 'CHOOSE OUTPOST TYPE',
        list: [
          {
            key: 'data',
            label: 'Outpost for Data',
            icon: <DataScanIcon />,
            onClick: () => navigate('/setup/outpost/add/data'),
          },
          {
            key: 'vulnerability',
            label: 'Outpost for Vulnerability',
            icon: <OutpostForVulnerabilityIcon />,
            onClick: () => navigate('/setup/outpost/add/vulnerability'),
          },
        ],
      },
    },
  ];

  return (
    <Card>
      <Stack h="full" spacing={5} w="full">
        <HStack w="full" justify="space-between">
          <PageHeaderWithIcon label="Outpost" icon={<DataScanIcon />} />
          <HStack>
            <LearnMoreButton
              onOpen={() =>
                setLearnMore(
                  viewBy.value === 'Ariksa'
                    ? 'vulnerability_scan'
                    : 'ariksa_outposts',
                )
              }
              label={
                viewBy.value === 'Ariksa'
                  ? 'Vulnerability Scan'
                  : 'Ariksa Outposts'
              }
            />
            {viewBy.value === 'Ariksa' ? (
              <AddButton
                label="Add Account"
                onClick={() =>
                  navigate('/setup/outpost/add/vulnerability/account')
                }
              />
            ) : (
              <Menu
                menuItems={menuItems}
                buttonLabel={<AddButton label="Deploy Outpost" />}
              />
            )}
          </HStack>
        </HStack>
        <HStack>
          <FilterLabelWrapper>VIEW BY</FilterLabelWrapper>
          <ButtonGroup
            items={outpostViewByOptions}
            value={viewBy}
            onClick={setViewBy}
          />
        </HStack>
        <BorderedBox h="full">
          <Stack h="full" spacing={5} w="full">
            {viewBy.value !== 'Ariksa' && (
              <HStack>
                <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
                <Box zIndex={900}>
                  <Select
                    options={state.accountOptions}
                    value={state.account}
                    onChange={s => updateState({ account: s })}
                    showIconInValueContainer
                    styles={{
                      menu: provided => ({
                        ...provided,
                        width: 'max-content',
                        minWidth: '100%',
                      }),
                    }}
                    isAFilter
                    leftMessage="Account"
                    showUndefinedValueLabel={false}
                    isDisabled={
                      vulnerabilityOutposts.isLoading || dataOutposts.isLoading
                    }
                  />
                </Box>
                <Box zIndex={900}>
                  <Select
                    options={state.regionOptions}
                    value={state.region}
                    onChange={s => updateState({ region: s })}
                    showIconInValueContainer
                    isDisabled={
                      vulnerabilityOutposts.isLoading || dataOutposts.isLoading
                    }
                    isAFilter
                    leftMessage="Region"
                    showUndefinedValueLabel={false}
                  />
                </Box>
                <Box zIndex={900}>
                  <Select
                    options={outpostTypeOptions}
                    value={state.outpostType}
                    onChange={s => updateState({ outpostType: s })}
                    showIconInValueContainer
                    isAFilter
                    isDisabled={
                      vulnerabilityOutposts.isLoading || dataOutposts.isLoading
                    }
                    leftMessage="Type"
                    showUndefinedValueLabel={false}
                  />
                </Box>
                <Box zIndex={900}>
                  <Select
                    options={outpostStatusOptions}
                    value={state.status}
                    onChange={s => updateState({ status: s })}
                    showIconInValueContainer
                    isDisabled={
                      vulnerabilityOutposts.isLoading || dataOutposts.isLoading
                    }
                    isAFilter
                    showClearIndicator={false}
                    leftMessage="Status"
                  />
                </Box>
              </HStack>
            )}

            <Box flex={1}>
              <Table
                data={
                  viewBy.value !== 'Ariksa'
                    ? state.sortedData
                    : ariksaOutposts.data
                }
                noDataMessage={
                  viewBy.value === 'Ariksa'
                    ? 'No Accounts Enabled'
                    : 'No ' + state.status?.value + ' Outposts'
                }
                columns={viewBy.value === 'Ariksa' ? accountsColumns : columns}
                isLoading={
                  vulnerabilityOutposts.isLoading ||
                  dataOutposts.isLoading ||
                  ariksaOutposts.isLoading
                }
                styles={{ header: { zIndex: 800 } }}
                onSort={sortInfo => {
                  if (!isEqual(state.sortByField, sortInfo)) {
                    updateState({ sortByField: sortInfo });
                  }
                }}
                {...(viewBy.value !== 'Ariksa' &&
                  state.status?.value === 'Active' && {
                    subComponent: ({ row, expanded }) => (
                      <OutpostSubComponent row={row} isExpanded={expanded} />
                    ),
                    onRowClick: row => updateState({ row }),
                  })}
              />
            </Box>
          </Stack>
          {updateRegionsDisclosure.isOpen && (
            <UpdateRegionsModal
              isOpen={updateRegionsDisclosure.isOpen}
              onClose={() => {
                updateRegionsDisclosure.onClose();
                updateState({ setGlobalRegions: false });
              }}
              row={currentRow}
            />
          )}
          {detachAccountDisclosure.isOpen && (
            <DetachAccountModal
              isOpen={detachAccountDisclosure.isOpen}
              onClose={detachAccountDisclosure.onClose}
              row={currentRow}
            />
          )}
        </BorderedBox>
      </Stack>
      <LearnMore />
    </Card>
  );
};
